import { Builder, BuilderComponent } from '@builder.io/react';
import { graphql } from 'gatsby';
import { Suspense } from 'react';
import customComponentGenerator, { sectionComponentDataModel } from 'templates/customComponentGenerator';

import Footer from 'components/Footer/Footer';
import GeneralHead from 'components/GeneralHead';
import Header from 'components/Header/Header';
import SEO from 'components/Seo/Seo';

import updateBuilderPixel from 'utils/updateBuilderPixel';

import type { Query } from 'graphqlTypes';
import type { FC } from 'react';

interface IHead {
  data: Query;
  location: Location;
}

const PageTemplate = (query: { data: Query }) => {
  const {
    data: {
      allBuilderModels: {
        onePage,
        oneGlobalNavigation: header,
        oneGlobalFooter: footer,
        blogCategories,
        learnCategories,
      },
    },
  } = query;

  const content = onePage?.content;
  updateBuilderPixel(content?.data?.blocks);
  customComponentGenerator();

  return (
    <Suspense>
      {header?.content && (
        <Header
          isScrollbarEnabled={onePage?.data?.hasScrollProgressBar}
          firstCompIsAlwaysDark={content.data.blocks?.[0]?.component?.options.alwaysDark}
          {...header?.content?.data}
        />
      )}
      <BuilderComponent
        content={content}
        model="page"
        data={{ resourceCategories: JSON.stringify({ blogCategories, learnCategories }) }}
        options={{ includeRefs: true, cachebust: true }}
      />
      {footer && <Footer footerType={onePage?.data?.footerType || 'Global Footer'} {...footer?.content?.data} />}
    </Suspense>
  );
};

export const Head: FC<IHead> = ({
  data: {
    allBuilderModels: { onePage },
  },
  location,
}) => (
  <>
    {onePage && (
      <GeneralHead>
        <SEO
          title={onePage?.data?.title}
          description={onePage?.data?.description}
          metaTitle={onePage?.data?.metaTitle}
          metaDescription={onePage?.data?.metaDescription}
          seoTitle={onePage?.data?.seoTitle}
          seoDescription={onePage?.data?.seoDescription}
          ogImage={onePage?.data?.ogImage}
          location={location}
          twitterTitle={onePage?.data?.twitterTitle}
          twitterDescription={onePage?.data?.twitterDescription}
          twitterImage={onePage?.data?.twitterImage}
          schemaMarkup={onePage?.data?.schemaMarkup}
          robots={onePage?.data?.robots}
        />
      </GeneralHead>
    )}
  </>
);

export default PageTemplate;

export const pageQuery = graphql`
  query buildPage($path: String!) {
    allBuilderModels {
      onePage(target: { urlPath: $path }, options: { includeRefs: true, cachebust: true }) {
        content
        data {
          title
          description
          metaTitle
          metaDescription
          footerType
          ogImage
          url
          twitterTitle
          twitterDescription
          twitterImage
          robots
          schemaMarkup
          seoTitle
          seoDescription
          hasScrollProgressBar
        }
      }
      oneGlobalFooter(
        options: { includeRefs: true, cachebust: true }
        target: { id: "dab6520bd33c4eb583740933f76a43e8" }
      ) {
        content
      }
      oneGlobalNavigation(
        options: { includeRefs: true, cachebust: true }
        target: { id: "8c58482d06a5431a965de853958b44f2" }
      ) {
        content
      }
      blogCategories(options: { includeRefs: true, cachebust: true }) {
        content
      }
      learnCategories(options: { includeRefs: true, cachebust: true }) {
        content
      }
    }
  }
`;

Builder.register('insertMenu', {
  name: 'Section Components',
  items: sectionComponentDataModel.map(model => ({ name: model.config.name })),
});
